import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { School, getSchools, getSchool, COLLECTIONS, generateId, Teacher, getTeachers } from '../../../utils/firebase';
import { db } from '../../../utils/firebase';
import { doc, setDoc, deleteDoc, updateDoc, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import InputField from '../../../components/ui/InputField';
import { searchComciganSchools, type ComciganSchool } from '../../../utils/comcigan';
import PlusIcon from '../../../components/icons/PlusIcon';
import EditIcon from '../../../components/icons/EditIcon';
import TrashIcon from '../../../components/icons/TrashIcon';
import CloseIcon from '../../../components/icons/CloseIcon';

interface SchoolFormData {
  name: string;
  address: string;
  code: string;
  type: 'elementary' | 'middle' | 'high';
  phone: string;
  adminId?: string;
  maxClassCount: number;
  gradeClasses: Record<string, number>;
  neisOfficeCode?: string;
  neisSchoolCode?: string;
  comciganSchoolCode?: string;
}

interface SchoolManagementProps {
  // 필요한 props가 있다면 여기에 추가
}

const SchoolManagement: React.FC<SchoolManagementProps> = () => {
  const { currentUser } = useAuth();
  const [schools, setSchools] = useState<School[]>([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<SchoolFormData>({
    name: '',
    address: '',
    code: '',
    type: 'elementary',
    phone: '',
    adminId: '',
    maxClassCount: 10,
    gradeClasses: {}
  });
  const [editingSchool, setEditingSchool] = useState<School | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [adminOptions, setAdminOptions] = useState<{ id: string; name: string }[]>([]);
  const [comciganQuery, setComciganQuery] = useState('');
  const [comciganResults, setComciganResults] = useState<ComciganSchool[]>([]);
  const [comciganSearching, setComciganSearching] = useState(false);
  const [comciganSearchError, setComciganSearchError] = useState<string | null>(null);

  useEffect(() => {
    loadSchools();
    loadTeachers();
  }, []);

  const loadSchools = async () => {
    try {
      setLoading(true);
      setError(null);
      const schoolsData = await getSchools();
      setSchools(schoolsData);
    } catch (err) {
      setError('학교 목록을 불러오는데 실패했습니다.');
      console.error('Error loading schools:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTeachers = async () => {
    try {
      const teachersData = await getTeachers();
      setTeachers(teachersData);
      
      // 관리자 옵션 설정 (superAdmin 또는 schoolAdmin 역할을 가진 교사)
      const adminTeachers = teachersData.filter(
        (teacher: Teacher) => teacher.role === 'superAdmin' || teacher.role === 'schoolAdmin'
      );
      
      setAdminOptions(
        adminTeachers.map((teacher: Teacher) => ({
          id: teacher.uid,
          name: teacher.name
        }))
      );
    } catch (err) {
      console.error('Error loading teachers:', err);
    }
  };

  // 6글자 랜덤 코드 생성 함수
  const generateSchoolCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handleOpen = (school?: School) => {
    if (school) {
      setEditingSchool(school);
      setFormData({
        name: school.name,
        address: school.address || '',
        code: school.code,
        type: school.type,
        phone: school.phone || '',
        adminId: school.adminId || '',
        maxClassCount: school.maxClassCount || 10,
        gradeClasses: school.gradeClasses || {},
        neisOfficeCode: school.neisOfficeCode || '',
        neisSchoolCode: school.neisSchoolCode || '',
        comciganSchoolCode: school.comciganSchoolCode ? String(school.comciganSchoolCode) : ''
      });
      setComciganQuery(school.name || '');
      setComciganResults([]);
      setComciganSearchError(null);
    } else {
      setEditingSchool(null);
      setFormData({
        name: '',
        address: '',
        code: generateSchoolCode(),
        type: 'elementary',
        phone: '',
        adminId: '',
        maxClassCount: 10,
        gradeClasses: {},
        neisOfficeCode: '',
        neisSchoolCode: '',
        comciganSchoolCode: ''
      });
      setComciganQuery('');
      setComciganResults([]);
      setComciganSearchError(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingSchool(null);
    setFormData({
      name: '',
      address: '',
      code: '',
      type: 'elementary',
      phone: '',
      adminId: '',
      maxClassCount: 10,
      gradeClasses: {},
      neisOfficeCode: '',
      neisSchoolCode: '',
      comciganSchoolCode: ''
    });
    setComciganQuery('');
    setComciganResults([]);
    setComciganSearchError(null);
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // 필수 필드 검증
      if (!formData.name.trim()) {
        setError('학교 이름은 필수 입력 항목입니다.');
        setLoading(false);
        return;
      }
      
      if (!formData.code.trim()) {
        setError('학교 코드는 필수 입력 항목입니다.');
        setLoading(false);
        return;
      }

      // 학교 코드를 문서 ID로 사용
      const schoolId = formData.code;

      const schoolData: School = {
        id: schoolId,
        name: formData.name,
        address: formData.address,
        code: formData.code,
        type: formData.type,
        phone: formData.phone,
        adminId: formData.adminId,
        maxClassCount: formData.maxClassCount,
        isActive: true,
        neisOfficeCode: formData.neisOfficeCode?.trim() || '',
        neisSchoolCode: formData.neisSchoolCode?.trim() || '',
        comciganSchoolCode: Number.parseInt(formData.comciganSchoolCode || '', 10) || 0,
        createdAt: editingSchool?.createdAt || new Date(),
        updatedAt: new Date(),
        gradeClasses: formData.gradeClasses
      };

      await setDoc(doc(db, COLLECTIONS.SCHOOLS, schoolId), schoolData);

      setSuccess(editingSchool ? '학교 정보가 수정되었습니다.' : '새로운 학교가 추가되었습니다.');
      handleClose();
      loadSchools();
    } catch (err) {
      setError(err instanceof Error ? err.message : '학교 정보 저장에 실패했습니다.');
      console.error('Error saving school:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (schoolId: string) => {
    if (!window.confirm('정말로 이 학교를 삭제하시겠습니까?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await deleteDoc(doc(db, COLLECTIONS.SCHOOLS, schoolId));

      setSuccess('학교가 삭제되었습니다.');
      loadSchools();
    } catch (err) {
      setError('학교 삭제에 실패했습니다.');
      console.error('Error deleting school:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleComciganSearch = async () => {
    const queryText = (comciganQuery || formData.name || '').trim();
    if (!queryText) {
      setComciganSearchError('검색할 학교 이름을 입력해 주세요.');
      return;
    }
    setComciganSearching(true);
    setComciganSearchError(null);
    try {
      const schools = await searchComciganSchools(queryText);
      setComciganResults(schools);
      if (schools.length === 0) {
        setComciganSearchError('검색 결과가 없습니다. 학교 이름을 다시 확인해 주세요.');
      }
    } catch (err) {
      setComciganResults([]);
      setComciganSearchError(err instanceof Error ? err.message : '컴시간 학교 검색에 실패했습니다.');
    } finally {
      setComciganSearching(false);
    }
  };

  const getGradeRange = () => {
    switch (formData.type) {
      case 'elementary':
        return [1, 2, 3, 4, 5, 6];
      case 'middle':
      case 'high':
        return [1, 2, 3];
      default:
        return [1, 2, 3, 4, 5, 6];
    }
  };

  const handleGradeClassChange = (grade: number, value: string) => {
    const classCount = parseInt(value) || 0;
    setFormData(prev => ({
      ...prev,
      gradeClasses: {
        ...prev.gradeClasses,
        [grade]: classCount
      }
    }));
  };

  const getSchoolTypeLabel = (type: string) => {
    switch (type) {
      case 'elementary':
        return '초등학교';
      case 'middle':
        return '중학교';
      case 'high':
        return '고등학교';
      default:
        return type;
    }
  };

  if (loading && schools.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '200px' 
      }}>
        <div style={{ fontSize: '16px', color: 'var(--Color-text-normal, #161616)' }}>
          로딩 중...
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div
          style={{
            fontSize: '24px',
            fontWeight: 600,
            lineHeight: '36px',
            color: 'var(--Color-text-strong, #0D0D0D)'
          }}
        >
          학교 관리
        </div>
        <Button
          variant="primary"
          size="medium"
          leftIcon={<PlusIcon width={16} height={16} fill="white" />}
          onClick={() => handleOpen()}
        >
          새 학교 추가
        </Button>
      </div>

      {/* 알림 메시지 */}
      {error && (
        <Card
          padding="12px 16px"
          background="#FFF5F5"
          style={{ border: '1px solid #FFE0E0' }}
        >
          <div style={{ fontSize: '14px', color: '#FF3838' }}>{error}</div>
        </Card>
      )}

      {success && (
        <Card
          padding="12px 16px"
          background="#F0FFF4"
          style={{ border: '1px solid #C6F6D5' }}
        >
          <div style={{ fontSize: '14px', color: '#38A169' }}>{success}</div>
        </Card>
      )}

      {/* 학교 목록 테이블 */}
      <Card padding="0" style={{ overflow: 'hidden', width: '100%' }}>
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table style={{ width: '100%', minWidth: '800px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--Color-fill-hover, #F5F5F5)' }}>
                <th style={{
                  padding: '12px 20px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--Color-text-strong, #0D0D0D)',
                  borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                  whiteSpace: 'nowrap'
                }}>
                  학교명
                </th>
                <th style={{
                  padding: '12px 20px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--Color-text-strong, #0D0D0D)',
                  borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                  minWidth: '200px'
                }}>
                  주소
                </th>
                <th style={{
                  padding: '12px 20px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--Color-text-strong, #0D0D0D)',
                  borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                  whiteSpace: 'nowrap'
                }}>
                  학교 코드
                </th>
                <th style={{
                  padding: '12px 20px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--Color-text-strong, #0D0D0D)',
                  borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                  whiteSpace: 'nowrap'
                }}>
                  학교 유형
                </th>
                <th style={{
                  padding: '12px 20px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--Color-text-strong, #0D0D0D)',
                  borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                  whiteSpace: 'nowrap'
                }}>
                  담당자
                </th>
                <th style={{
                  padding: '12px 20px',
                  textAlign: 'center',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--Color-text-strong, #0D0D0D)',
                  borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                  whiteSpace: 'nowrap'
                }}>
                  관리
                </th>
              </tr>
            </thead>
            <tbody>
              {schools.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{
                    padding: '40px',
                    textAlign: 'center',
                    fontSize: '14px',
                    color: 'var(--Color-text-normal, #161616)'
                  }}>
                    등록된 학교가 없습니다.
                  </td>
                </tr>
              ) : (
                schools.map((school) => (
                  <tr key={school.id} style={{
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
                  }}>
                    <td style={{
                      padding: '12px 20px',
                      fontSize: '14px',
                      color: 'var(--Color-text-normal, #161616)',
                      whiteSpace: 'nowrap'
                    }}>
                      {school.name}
                    </td>
                    <td style={{
                      padding: '12px 20px',
                      fontSize: '14px',
                      color: 'var(--Color-text-normal, #161616)',
                      minWidth: '200px'
                    }}>
                      {school.address || '-'}
                    </td>
                    <td style={{
                      padding: '12px 20px',
                      fontSize: '14px',
                      color: 'var(--Color-text-normal, #161616)',
                      whiteSpace: 'nowrap'
                    }}>
                      {school.code}
                    </td>
                    <td style={{
                      padding: '12px 20px',
                      fontSize: '14px',
                      color: 'var(--Color-text-normal, #161616)',
                      whiteSpace: 'nowrap'
                    }}>
                      {getSchoolTypeLabel(school.type)}
                    </td>
                    <td style={{
                      padding: '12px 20px',
                      fontSize: '14px',
                      color: 'var(--Color-text-normal, #161616)',
                      whiteSpace: 'nowrap'
                    }}>
                      {school.adminId ? 
                        adminOptions.find(admin => admin.id === school.adminId)?.name || '담당자 정보 없음' 
                        : '미지정'}
                    </td>
                    <td style={{
                      padding: '12px 20px',
                      textAlign: 'center',
                      whiteSpace: 'nowrap'
                    }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          type="button"
                          onClick={() => handleOpen(school)}
                          style={{
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--Color-fill-hover, #F5F5F5)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <EditIcon width={16} height={16} color="#4C4C4C" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(school.id)}
                          style={{
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#FFF5F5';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <TrashIcon width={14} height={16} color="#FF3838" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* 다이얼로그 */}
      {open && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            fontFamily: 'Pretendard, sans-serif'
          }}
          onClick={handleClose}
        >
          <div
            style={{
              width: '90%',
              maxWidth: '800px',
              padding: '0',
              borderRadius: '16px',
              background: 'var(--Color-background-normal, white)',
              boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
              position: 'relative',
              overflow: 'hidden',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
          >
            {/* 다이얼로그 헤더 */}
            <div style={{
              padding: '32px',
              borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{
                fontSize: '20px',
                fontWeight: 600,
                color: 'var(--Color-text-strong, #0D0D0D)'
              }}>
                {editingSchool ? '학교 정보 수정' : '새 학교 추가'}
              </div>
              <button
                type="button"
                onClick={handleClose}
                style={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--Color-fill-hover, #F5F5F5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <CloseIcon width={20} height={20} />
              </button>
            </div>

            {/* 다이얼로그 내용 */}
            <div style={{
              padding: '32px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
              <InputField
                label="학교 명"
                value={formData.name}
                onChange={(value) => setFormData({ ...formData, name: value })}
                required
                width="100%"
              />

              <InputField
                label="주소"
                value={formData.address}
                onChange={(value) => setFormData({ ...formData, address: value })}
                width="100%"
              />

              <InputField
                label="학교 코드"
                value={formData.code}
                onChange={(value) => setFormData({ ...formData, code: value })}
                required
                width="100%"
              />

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#161616',
                  marginBottom: '8px'
                }}>
                  학교 유형
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => {
                    const newType = e.target.value as School['type'];
                    setFormData({ 
                      ...formData, 
                      type: newType,
                      gradeClasses: {} // 학교 유형이 변경되면 학급 설정 초기화
                    });
                  }}
                  style={{
                    width: '100%',
                    height: '48px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: '#161616',
                    backgroundColor: 'white',
                    border: '1.2px solid #F0F0F0',
                    borderRadius: '8px',
                    outline: 'none',
                    cursor: 'pointer',
                    fontFamily: 'Pretendard, sans-serif'
                  }}
                >
                  <option value="elementary">초등학교</option>
                  <option value="middle">중학교</option>
                  <option value="high">고등학교</option>
                </select>
              </div>

              <InputField
                label="학교 전화번호"
                value={formData.phone}
                onChange={(value) => setFormData({ ...formData, phone: value })}
                width="100%"
              />

              <div style={{
                padding: '12px 16px',
                backgroundColor: 'var(--Color-background-sub, #F8F8F8)',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--Color-text-strong, #0D0D0D)' }}>
                  나이스(NEIS) 시간표 연동 (선택)
                </div>
                <div style={{ fontSize: '12px', color: 'var(--Color-text-normal, #161616)', marginBottom: '4px' }}>
                  나이스교육정보개방포털(open.neis.go.kr)에서 발급한 학교 코드를 입력하면 시간표를 자동으로 불러올 수 있습니다.
                </div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <InputField
                    label="시도교육청코드"
                    value={formData.neisOfficeCode || ''}
                    onChange={(value) => setFormData({ ...formData, neisOfficeCode: value })}
                    placeholder="예: B10 (서울), J10 (경기)"
                    width="180px"
                  />
                  <InputField
                    label="표준학교코드"
                    value={formData.neisSchoolCode || ''}
                    onChange={(value) => setFormData({ ...formData, neisSchoolCode: value })}
                    placeholder="예: 7010911"
                    width="180px"
                  />
                </div>
              </div>

              <div style={{
                padding: '12px 16px',
                backgroundColor: 'var(--Color-background-sub, #F8F8F8)',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--Color-text-strong, #0D0D0D)' }}>
                  컴시간알리미 시간표 연동 (선택)
                </div>
                <div style={{ fontSize: '12px', color: 'var(--Color-text-normal, #161616)' }}>
                  컴시간을 쓰는 학교라면 학교 이름으로 검색해 선택하세요. 나이스보다 이번 주 변경 시간표를 잘 반영합니다.
                </div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <InputField
                    label="학교 이름 검색"
                    value={comciganQuery}
                    onChange={setComciganQuery}
                    placeholder={formData.name || '예: 경기북과학고'}
                    width="220px"
                  />
                  <Button
                    variant="outline"
                    size="medium"
                    onClick={handleComciganSearch}
                    disabled={comciganSearching}
                  >
                    {comciganSearching ? '검색 중...' : '컴시간에서 찾기'}
                  </Button>
                  <InputField
                    label="컴시간 학교 코드"
                    value={formData.comciganSchoolCode || ''}
                    onChange={(value) => setFormData({ ...formData, comciganSchoolCode: value })}
                    placeholder="검색 후 자동 입력"
                    width="160px"
                  />
                </div>
                {comciganSearchError && (
                  <div style={{ fontSize: '13px', color: '#C53030' }}>
                    {comciganSearchError}
                  </div>
                )}
                {comciganResults.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {comciganResults.slice(0, 8).map((school) => {
                      const selected = String(school.code) === (formData.comciganSchoolCode || '');
                      return (
                        <button
                          key={`${school.region}-${school.code}`}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, comciganSchoolCode: String(school.code) });
                            setComciganQuery(school.name);
                          }}
                          style={{
                            textAlign: 'left',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: selected ? '1px solid var(--Color-fill-brand-color, #FF6F0F)' : '1px solid var(--Color-line-normal, #F0F0F0)',
                            backgroundColor: selected ? '#FFF4EC' : 'white',
                            cursor: 'pointer',
                            fontFamily: 'Pretendard, sans-serif',
                            fontSize: '13px',
                            color: 'var(--Color-text-normal, #161616)'
                          }}
                        >
                          {school.name} · {school.region} · {school.code}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: 'var(--Color-text-strong, #0D0D0D)'
                }}>
                  학년별 학급 수 설정
                </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                {getGradeRange().map(grade => (
                  <div key={grade} style={{ width: '180px', flex: '1 1 auto', minWidth: '150px' }}>
                    <InputField
                      label={`${grade}학년 학급 수`}
                      type="number"
                      value={formData.gradeClasses[grade]?.toString() || ''}
                      onChange={(value) => handleGradeClassChange(grade, value)}
                      width="100%"
                    />
                  </div>
                ))}
              </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#161616',
                  marginBottom: '8px'
                }}>
                  학교 담당자
                </label>
                <select
                  value={formData.adminId || ''}
                  onChange={(e) => setFormData({ ...formData, adminId: e.target.value })}
                  style={{
                    width: '100%',
                    height: '48px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: '#161616',
                    backgroundColor: 'white',
                    border: '1.2px solid #F0F0F0',
                    borderRadius: '8px',
                    outline: 'none',
                    cursor: 'pointer',
                    fontFamily: 'Pretendard, sans-serif'
                  }}
                >
                  <option value="">미지정</option>
                  {adminOptions.map(admin => (
                    <option key={admin.id} value={admin.id}>
                      {admin.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* 다이얼로그 푸터 */}
            <div style={{
              padding: '24px 32px',
              borderTop: '1px solid var(--Color-line-normal, #F0F0F0)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <Button
                variant="cancel"
                onClick={handleClose}
                disabled={loading}
              >
                취소
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? '처리 중...' : (editingSchool ? '수정' : '추가')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolManagement;
